import { Text, View, Pressable } from 'react-native';
import { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from "@legendapp/list";
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import Table from '@/components/General/Table/Table';
import Chip from '@/components/General/Chip';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import usePlatform from '@/utils/hooks/usePlatform';
import { formatCampaignDate, senderName, senderProfile, statusColorVariant } from './campaignHelpers';
import CampaignDetailSheet from './CampaignDetailSheet';
import SMSCampaignRecipientsSection from './SMSCampaignRecipientsSection';

/**
 * SMS Campaigns tab on the Connect screen — past mass SMS sends.
 * Native: card list. Web: table. Row press opens a detail sheet with all fields.
 */
const SMSCampaignsList = () => {
    const queryClient = useQueryClient();
    const { isNative } = usePlatform();
    const { data: campaigns, isLoading: loadingCampaigns } = Query.smsCampaigns.all();
    const [searchText, setSearchText] = useState('');
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    const filteredCampaigns = useMemo(() => {
        const all = campaigns || [];
        if(!searchText) return all;
        const q = searchText.toLowerCase();
        return all.filter(c => c.messageBody?.toLowerCase().includes(q) || senderName(c).toLowerCase().includes(q) || c.status?.toLowerCase().includes(q));
    }, [campaigns, searchText]);

    const handleRefresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.smsCampaigns.all() });

    const columns = useMemo(() => [
        {
            value: 'Message',
            accessor: 'messageBody',
            minWidth: 260,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellTitle} numberOfLines={1}>{item.messageBody}</Text>
                </View>
            ),
        },
        {
            value: 'Sent By',
            accessor: 'Sender.firstName',
            minWidth: 150,
            renderer: (item) => {
                const profile = senderProfile(item);
                const name = senderName(item);
                return (
                    <View style={styles.senderCell}>
                        {profile ? (
                            <Avatar size={26} profile={profile} disableBorder />
                        ) : (
                            <View style={styles.systemAvatar}>
                                <Unicon name="hardware-chip-outline" size={14} colorVariant="textSecondary" />
                            </View>
                        )}
                        <Text style={[styles.cellText, !profile && styles.cellTextSecondary]} numberOfLines={1}>{name}</Text>
                    </View>
                );
            },
        },
        {
            value: 'Date',
            accessor: 'createdAt',
            minWidth: 170,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText} numberOfLines={1}>{formatCampaignDate(item.createdAt)}</Text>
                </View>
            ),
        },
        {
            value: 'Status',
            accessor: 'status',
            minWidth: 110,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Chip colorVariant={statusColorVariant(item.status)} style={styles.statusChip}>{item.status || '—'}</Chip>
                </View>
            ),
        },
        {
            value: 'Recipients',
            accessor: 'recipientCount',
            minWidth: 90,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText}>{item.recipientCount ?? 0}</Text>
                </View>
            ),
        },
        {
            value: 'Sent',
            accessor: 'sentCount',
            minWidth: 70,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={styles.cellText}>{item.sentCount ?? 0}</Text>
                </View>
            ),
        },
        {
            value: 'Failed',
            accessor: 'failedCount',
            minWidth: 70,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={[styles.cellText, (item.failedCount ?? 0) > 0 && styles.cellTextColored('error')]}>{item.failedCount ?? 0}</Text>
                </View>
            ),
        },
        {
            value: 'Skipped',
            accessor: 'skippedCount',
            minWidth: 80,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    <Text style={[styles.cellText, (item.skippedCount ?? 0) > 0 && styles.cellTextColored('warning')]}>{item.skippedCount ?? 0}</Text>
                </View>
            ),
        },
    ], []);

    const renderItem = ({ item }) => {
        return (
            <Pressable testID={`sms-campaign-row-${item.id}`} style={styles.card} onPress={() => setSelectedCampaign(item)}>
                <View style={styles.cardHeader}>
                    <View style={styles.iconWrap}><Unicon name="chatbubble-outline" size={19} colorVariant="connect" /></View>
                    <View style={styles.textContainer}>
                        <Text style={styles.title} numberOfLines={2}>{item.messageBody}</Text>
                        <Text style={styles.subtitle} numberOfLines={1}>{formatCampaignDate(item.createdAt)} · {senderName(item)}</Text>
                    </View>
                    <Unicon name="chevron-forward-outline" size={18} colorVariant="textSecondary" />
                </View>
                <View style={styles.cardMetrics}>
                    <Chip colorVariant="success">{item.sentCount ?? 0} sent</Chip>
                    {(item.failedCount ?? 0) > 0 && <Chip colorVariant="error">{item.failedCount} failed</Chip>}
                    {(item.skippedCount ?? 0) > 0 && <Chip colorVariant="warning">{item.skippedCount} skipped</Chip>}
                </View>
            </Pressable>
        );
    };

    let contentLoaders = [];
    if(loadingCampaigns) {
        for(let i = 0; i < 8; i++) {
            contentLoaders.push(<View key={"loader" + i} style={styles.loaderRow}><ContentLoader containerHeight={60} staggerPosition={i} /></View>);
        }
    }

    return (
        <View style={styles.container}>
            {isNative && (
                <View style={styles.searchContainer}>
                    <SearchBox
                        searchText={searchText}
                        onChangeText={setSearchText}
                        placeholder="Search SMS campaigns"
                        textChangeDelay={150}
                        testID="SMSCampaignsList"
                    />
                </View>
            )}
            {isNative ? (
                <LegendList
                    recycleItems
                    estimatedItemSize={106}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={handleRefresh}
                    keyExtractor={(item) => item.id}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    data={loadingCampaigns ? [] : filteredCampaigns}
                    renderItem={renderItem}
                    ListFooterComponent={!loadingCampaigns
                        ? <View style={styles.footer}>{filteredCampaigns.length == 0 ? <EmptyListPlaceholder spacing={10} message={searchText ? "No SMS campaigns match your search" : "No SMS campaigns yet!"} /> : <View />}</View>
                        : <View style={styles.footerLoading}>{contentLoaders}</View>}
                />
            ) : (
                <Table
                    title="SMS Campaigns"
                    tableId="sms-campaigns"
                    columns={columns}
                    data={filteredCampaigns}
                    isLoading={loadingCampaigns}
                    onRowClick={(item) => setSelectedCampaign(item)}
                    onRefresh={handleRefresh}
                    onSearchChange={setSearchText}
                    estimatedItemSize={52}
                />
            )}

            <CampaignDetailSheet
                campaign={selectedCampaign}
                onClose={() => setSelectedCampaign(null)}
                modalTitle="SMS Campaign"
                icon="chatbubble-outline"
                body={selectedCampaign?.messageBody}
                testID="sms-campaign-detail"
                headerChips={selectedCampaign ? [{ label: 'Segments', value: `${selectedCampaign.messageSegments ?? 0} segments`, icon: 'layers-outline' }] : []}
                stats={selectedCampaign ? [
                    { label: 'Sent', value: selectedCampaign.sentCount ?? 0, colorVariant: 'success' },
                    { label: 'Failed', value: selectedCampaign.failedCount ?? 0, colorVariant: (selectedCampaign.failedCount ?? 0) > 0 ? 'error' : undefined },
                    { label: 'Skipped', value: selectedCampaign.skippedCount ?? 0, colorVariant: (selectedCampaign.skippedCount ?? 0) > 0 ? 'warning' : undefined },
                ] : []}
                recipientsSection={selectedCampaign ? <SMSCampaignRecipientsSection campaignID={selectedCampaign.id} /> : null}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
    },
    searchContainer: {
        paddingHorizontal: 8,
        paddingTop: 10,
        paddingBottom: 4,
    },
    card: {
        padding: 12,
        marginHorizontal: 6,
        marginVertical: 5,
        borderRadius: 16,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: 10,
        _web: {
            transition: "all 0.15s ease",
            _hover: { backgroundColor: colors.hoverCard, transform: [{ scale: 1.01 }], boxShadow: shadows.cardHover },
        },
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    iconWrap: {
        width: 38,
        height: 38,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.connectContainer,
    },
    textContainer: {
        flex: 1,
        gap: 3,
        minWidth: 0,
    },
    title: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    subtitle: {
        fontSize: 11,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
    },
    cardMetrics: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        paddingLeft: 48,
    },
    statusChip: {
        textTransform: 'capitalize',
        alignSelf: 'flex-start',
    },
    cellContainer: {
        justifyContent: 'center',
        paddingVertical: 10,
        minHeight: 52,
    },
    senderCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 8,
        minHeight: 52,
    },
    systemAvatar: {
        width: 26,
        height: 26,
        borderRadius: 13,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
    },
    cellTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    cellText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    cellTextSecondary: {
        color: colors.textSecondary,
    },
    cellTextColored: (colorVariant) => ({
        color: colors[colorVariant],
        fontFamily: fonts.bold,
    }),
    loaderRow: {
        marginHorizontal: 10,
        marginVertical: 6,
    },
    footer: {
        paddingBottom: 40,
    },
    footerLoading: {
        paddingBottom: 20,
    },
}));

export default SMSCampaignsList;
